import { useEffect, useMemo, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import logoATR from "../assets/logoatrputih.png";

const ITEMS_PER_PAGE = 10;

const categoryStyles = {
  "8K": "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30",
  "18K": "bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/30",
  "32K": "bg-violet-500/15 text-violet-300 ring-1 ring-violet-400/30",
  "50K": "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30",
};

const statusStyles = {
  FINISH: "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30",
  DNF: "bg-red-500/15 text-red-300 ring-1 ring-red-400/30",
  DNS: "bg-slate-500/15 text-slate-300 ring-1 ring-slate-400/30",
  RUNNING: "bg-blue-500/15 text-blue-300 ring-1 ring-blue-400/30",
};

const winnerStyles = {
  1: "bg-yellow-400/20 text-yellow-300 ring-1 ring-yellow-300/40",
  2: "bg-slate-300/20 text-slate-200 ring-1 ring-slate-200/40",
  3: "bg-orange-400/20 text-orange-300 ring-1 ring-orange-300/40",
};

const categories = ["8K", "18K", "32K", "50K"];
const genders = ["M", "F"];

export default function ResultPeserta() {
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState("");

  // Default tampilan awal
  const [selectedCategory, setSelectedCategory] = useState("8K");
  const [selectedGender, setSelectedGender] = useState("M");

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/data/result.json")
      .then((res) => {
        if (!res.ok) {
          throw new Error("File result.json tidak ditemukan");
        }

        return res.json();
      })
      .then((data) => {
        const formattedData = data.map((item, index) => {
          const fullName = item.name || item.nama || "";
          const nameParts = fullName.trim().split(" ");

          const firstName = nameParts[0] || "-";
          const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(" ") : "-";

          return {
            no: item.no || index + 1,
            bib: item.bib || item.BIB || "-",
            firstName,
            lastName,
            category: item.category || item.kategori || "-",
            gender: String(
              item.gender || item.jenis_kelamin || "-"
            ).toUpperCase(),
            time: item.time || item.waktu || item.finish_time || "-",
            status:
              item.status ||
              item.status_result ||
              item.result_status ||
              "-",
          };
        });

        // Hitung winner berdasarkan kategori + gender
        // Hanya peserta FINISH yang dihitung
        // Karena result.json sudah diurutkan berdasarkan official time,
        // maka ranking mengikuti urutan data dari file JSON.
        const rankCounter = {};

        const rankedData = formattedData.map((participant) => {
          const status = String(participant.status).toUpperCase();
          const gender = String(participant.gender).toUpperCase();
          const key = `${participant.category}-${gender}`;

          if (status === "FINISH") {
            rankCounter[key] = (rankCounter[key] || 0) + 1;

            return {
              ...participant,
              winnerRank: rankCounter[key] <= 3 ? rankCounter[key] : null,
            };
          }

          return {
            ...participant,
            winnerRank: null,
          };
        });

        setResults(rankedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Gagal mengambil data result:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedCategory, selectedGender]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return results.filter((p) => {
      const matchSearch = `
        ${p.bib}
        ${p.firstName}
        ${p.lastName}
        ${p.category}
        ${p.gender}
        ${p.time}
        ${p.status}
      `
        .toLowerCase()
        .includes(q);

      const matchCategory = p.category === selectedCategory;
      const matchGender = p.gender === selectedGender;

      return matchSearch && matchCategory && matchGender;
    });
  }, [query, selectedCategory, selectedGender, results]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;

  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    return filtered.slice(startIndex, endIndex);
  }, [filtered, currentPage]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#0a0d14] text-slate-100">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(56,120,255,0.25), transparent 60%), radial-gradient(ellipse 50% 40% at 90% 110%, rgba(14,165,233,0.18), transparent 60%)",
        }}
      />

      <svg
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[55vh] w-full opacity-[0.10]"
        viewBox="0 0 1440 600"
        preserveAspectRatio="none"
      >
        <path
          d="M0 480 L160 320 L300 420 L450 240 L620 380 L760 200 L920 360 L1080 260 L1240 400 L1440 300 L1440 600 L0 600 Z"
          fill="#3b82f6"
        />
        <path
          d="M0 540 L180 420 L340 500 L520 380 L700 480 L880 360 L1060 460 L1240 380 L1440 460 L1440 600 L0 600 Z"
          fill="#0ea5e9"
        />
      </svg>

      <div className="relative mx-auto max-w-7xl px-4 py-12 sm:py-16 md:px-6">
        <header className="flex flex-col items-center text-center">
          <div
            className="mb-6 inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
            style={{ boxShadow: "0 0 40px rgba(59,130,246,0.25)" }}
          >
            <img
              src={logoATR}
              alt="Alas Trail Run Logo"
              className="h-14 w-14 object-contain"
            />
          </div>

          <h1 className="bg-gradient-to-r from-blue-400 via-sky-300 to-cyan-300 bg-clip-text text-4xl font-black tracking-tight text-transparent sm:text-6xl md:text-7xl">
            ALAS TRAIL RUN
          </h1>

          <p className="mt-3 text-sm font-medium uppercase tracking-[0.4em] text-slate-400 sm:text-base">
            Result Peserta
          </p>
        </header>

        {/* Search */}
        <div className="mt-10">
          <div className="relative mx-auto max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama, BIB, kategori, gender, waktu, status..."
              className="w-full rounded-full border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-slate-100 placeholder:text-slate-500 backdrop-blur-xl outline-none transition focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/30"
            />
          </div>
        </div>

        {/* Filter kategori */}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {categories.map((category) => {
            const isActive = selectedCategory === category;

            return (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`rounded-xl border px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-all ${
                  isActive
                    ? "border-blue-400 bg-blue-500 text-white shadow-lg shadow-blue-500/25"
                    : "border-white/10 bg-white/5 text-slate-300 hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white"
                }`}
              >
                {`ATR ${category}`}
              </button>
            );
          })}
        </div>

        {/* Filter gender */}
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          {genders.map((gender) => {
            const isActive = selectedGender === gender;

            return (
              <button
                key={gender}
                type="button"
                onClick={() => setSelectedGender(gender)}
                className={`rounded-xl border px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-all ${
                  isActive
                    ? "border-cyan-400 bg-cyan-500 text-white shadow-lg shadow-cyan-500/25"
                    : "border-white/10 bg-white/5 text-slate-300 hover:border-cyan-400/60 hover:bg-cyan-500/10 hover:text-white"
                }`}
              >
                {gender === "M" ? "MALE" : "FEMALE"}
              </button>
            );
          })}
        </div>

        <section
          className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl"
          style={{ boxShadow: "0 0 40px rgba(59,130,246,0.15)" }}
        >
          {/* Header desktop */}
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 md:grid">
            <div className="col-span-1">BIB</div>
            <div className="col-span-2">Nama Depan</div>
            <div className="col-span-3">Nama Belakang</div>
            <div className="col-span-1">Kategori</div>
            <div className="col-span-1">Gender</div>
            <div className="col-span-1">Waktu</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2">Winner</div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-slate-400">
              Memuat data result peserta...
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {paginatedResults.map((p) => (
                <li
                  key={`${p.no}-${p.bib}`}
                  className="group grid grid-cols-2 gap-y-3 px-4 py-4 transition-all hover:bg-blue-500/5 md:grid-cols-12 md:gap-4 md:px-6"
                >
                  <div className="col-span-1 md:col-span-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      BIB
                    </div>
                    <div className="font-black text-blue-300">{p.bib}</div>
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Nama Depan
                    </div>
                    <div className="font-semibold text-slate-100">
                      {p.firstName}
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Nama Belakang
                    </div>
                    <div className="font-medium text-slate-200">
                      {p.lastName}
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-1">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Kategori
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold tracking-wide ${
                        categoryStyles[p.category] ||
                        "bg-slate-500/15 text-slate-300 ring-1 ring-slate-400/30"
                      }`}
                    >
                      {p.category}
                    </span>
                  </div>

                  <div className="col-span-1 md:col-span-1">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Gender
                    </div>
                    <span className="text-sm text-slate-400">{p.gender}</span>
                  </div>

                  <div className="col-span-1 md:col-span-1">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Waktu
                    </div>
                    <span className="font-mono text-sm font-semibold text-cyan-300">
                      {p.time}
                    </span>
                  </div>

                  <div className="col-span-1 md:col-span-1">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Status
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${
                        statusStyles[String(p.status).toUpperCase()] ||
                        "bg-slate-500/15 text-slate-300 ring-1 ring-slate-400/30"
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>

                  <div className="col-span-2 md:col-span-2">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500 md:hidden">
                      Winner
                    </div>

                    {p.winnerRank ? (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black uppercase tracking-wide ${
                          winnerStyles[p.winnerRank]
                        }`}
                      >
                        <Trophy className="h-3.5 w-3.5" />
                        Winner {p.winnerRank}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600">-</span>
                    )}
                  </div>
                </li>
              ))}

              {paginatedResults.length === 0 && (
                <li className="px-6 py-12 text-center text-sm text-slate-400">
                  Tidak ada result peserta yang cocok dengan pencarian.
                </li>
              )}
            </ul>
          )}
        </section>

        {!loading && filtered.length > ITEMS_PER_PAGE && (
          <div className="mt-8 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-slate-500">
              Halaman {currentPage} dari {totalPages}
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .slice(
                  Math.max(currentPage - 3, 0),
                  Math.min(currentPage + 2, totalPages)
                )
                .map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`h-10 w-10 rounded-xl border text-sm font-bold transition ${
                      currentPage === page
                        ? "border-blue-400 bg-blue-500 text-white shadow-lg shadow-blue-500/25"
                        : "border-white/10 bg-white/5 text-slate-300 hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white"
                    }`}
                  >
                    {page}
                  </button>
                ))}

              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-blue-400/60 hover:bg-blue-500/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <footer className="mt-10 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Alas Trail Run · Run with the wild.
        </footer>
      </div>
    </main>
  );
}